import { useState } from "react";
import { toast } from "react-toastify";

interface UsePaginationProps {
  totalPages?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
}

export const usePagination = ({
  totalPages = 0,
  currentPage = 1,
  onPageChange,
}: UsePaginationProps) => {
  const [current, setCurrent] = useState(currentPage);
  const [goPageNum, setGoPageNum] = useState("");

  const handlePageClick = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrent(page);
      if (onPageChange) {
        onPageChange(page);
      }
    }
  };

  const handleGoToPage = () => {
    const page = Number(goPageNum);
    if (page > 0 && page <= totalPages) {
      handlePageClick(page);
      setGoPageNum("");
    } else if (page > totalPages) {
      toast.error(`You can go up to ${totalPages} pages only`);
      setGoPageNum("");
      window.scrollTo(0, 0);
    }
  };

  const getVisiblePages = () => {
    const pages: number[] = [];
    
    // Show page 1 if only 1 page
    if (totalPages === 1) {
      pages.push(1);
      return pages;
    }
    
    // Don't show pagination if no pages
    if (totalPages < 1) {
      return pages;
    }
    
    // Specific pattern based on current page, but always respect totalPages
    if (current === 1) {
      pages.push(1);
      if (totalPages >= 2) pages.push(2);
    } else if (current === 2) {
      pages.push(1);
      if (totalPages >= 2) pages.push(2);
      if (totalPages >= 3) pages.push(3);
    } else if (current === 3) {
      pages.push(1);
      if (totalPages >= 2) pages.push(2);
      if (totalPages >= 3) pages.push(3);
      if (totalPages >= 4) pages.push(4);
    } else {
      const startPage = Math.max(1, current - 2);
      const endPage = Math.min(totalPages, current + 1);
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  return {
    currentPage: current,
    setCurrent,
    goPageNum,
    setGoPageNum,
    visiblePages: getVisiblePages(),
    handlePageClick,
    handleGoToPage,
  };
};
